import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFetchBiOverviewQuery, useFetchTrafficDashboardQuery } from "../../../api/analyticsApi";
import { Btn, Notice, Segmented, Select, TextInput } from "../../catalogProductStudio/edit/StudioUi";
import { TrendChart } from "../AnalyticsCharts";
import { BarList, DataState, DataTable, KpiCard, Panel } from "../AnalyticsUi";
import { daysInRange, fill, formatMoney, formatNumber, formatPercent, isDateKey } from "../analyticsModel";

export const trafficFilters = (params) =>
  params.get("trafficPreset") === "custom"
    ? { preset: "custom", dateFrom: params.get("trafficFrom") || "", dateTo: params.get("trafficTo") || "" }
    : { preset: ["7", "28", "90"].includes(params.get("trafficPreset")) ? params.get("trafficPreset") : "28" };
export const validTrafficDates = (from, to) =>
  isDateKey(from) && isDateKey(to) && from <= to && daysInRange(from, to) <= 180;

function TrafficControls({ filters, range, onChange, tt }) {
  const [draft, setDraft] = useState({ dateFrom: filters.dateFrom || "", dateTo: filters.dateTo || "" });
  useEffect(() => {
    if (range) setDraft({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  }, [range]);
  return (
    <form
      className="an-range"
      onSubmit={(event) => {
        event.preventDefault();
        if (validTrafficDates(draft.dateFrom, draft.dateTo)) onChange({ preset: "custom", ...draft });
      }}
    >
      <label className="an-range__field">
        <span>{tt("range")}</span>
        <Select
          value={filters.preset}
          onChange={(preset) => {
            if (preset !== "custom") onChange({ preset });
          }}
        >
          {["7", "28", "90"].map((value) => (
            <option key={value} value={value}>
              {fill(tt("traffic_days"), { days: value })}
            </option>
          ))}
          <option value="custom">{tt("preset_custom")}</option>
        </Select>
      </label>
      <label className="an-range__field">
        <span>{tt("dateFrom")}</span>
        <TextInput
          type="date"
          value={draft.dateFrom}
          max={draft.dateTo}
          onChange={(dateFrom) => setDraft((d) => ({ ...d, dateFrom }))}
        />
      </label>
      <label className="an-range__field">
        <span>{tt("dateTo")}</span>
        <TextInput
          type="date"
          value={draft.dateTo}
          min={draft.dateFrom}
          onChange={(dateTo) => setDraft((d) => ({ ...d, dateTo }))}
        />
      </label>
      <Btn type="submit" disabled={!validTrafficDates(draft.dateFrom, draft.dateTo)}>
        {tt("apply")}
      </Btn>
      <p className="an-range__summary">{tt("traffic_completedDays")}</p>
    </form>
  );
}

const KPI_KEYS = ["activeUsers", "sessions", "newUsers", "engagementRate", "view_item", "purchase"];
export const TrafficKpis = ({ data, tt, lang }) => (
  <div className="an-kpis an-traffic-kpis">
    {KPI_KEYS.map((key) => (
      <KpiCard
        key={key}
        tt={tt}
        lang={lang}
        label={tt(`traffic_${key}`)}
        help={tt(`traffic_${key}_help`)}
        compared={data.kpis[key] || { value: null, previous: null, change: null }}
        display={(key === "engagementRate" ? formatPercent : formatNumber)(data.kpis[key]?.value, lang)}
      />
    ))}
  </div>
);

const SectionStatus = ({ data, section, tt }) =>
  !["live", "empty"].includes(data.sections[section]) ? (
    <p role="status" className="an-note is-warning">
      {tt(`traffic_state_${data.sections[section] || "unavailable"}`)}
    </p>
  ) : null;

function SalesComparison({ data, tt, lang }) {
  const query = useFetchBiOverviewQuery(
    { dateFrom: data.range.dateFrom, dateTo: data.range.dateTo },
    { refetchOnMountOrArgChange: true },
  );
  return (
    <Panel title={tt("traffic_salesComparison")} hint={tt("traffic_salesDisclaimer")}>
      <div className="an-kpis an-kpis--compact">
        <KpiCard
          tt={tt}
          lang={lang}
          label={tt("traffic_orderValue")}
          value={data.kpis.purchaseRevenue?.value ?? null}
          display={formatMoney(data.kpis.purchaseRevenue?.value, lang)}
        />
        <KpiCard
          tt={tt}
          lang={lang}
          label={tt("traffic_ledgerSales")}
          value={query.currentData?.kpis.netRevenue?.value ?? null}
          display={formatMoney(query.currentData?.kpis.netRevenue?.value, lang)}
        />
      </div>
      <Link to={`/admin/analytics?from=${data.range.dateFrom}&to=${data.range.dateTo}`} className="pc-link">
        {tt("salesIntelligenceTitle")}
      </Link>
    </Panel>
  );
}

export function TrafficDashboard({ data, tt, lang }) {
  const [metric, setMetric] = useState("sessions");
  const numeric = (key, percent = false) => ({
    key,
    label: tt(`traffic_${key}`),
    align: "end",
    render: (row) => (percent ? formatPercent : formatNumber)(row[key], lang),
  });
  const activity = data.events.map((event) => ({ ...event, label: tt(`traffic_${event.key}`) }));
  return (
    <>
      <p className="an-muted" role="status">
        GA4 · {data.timezone} · {data.range.dateFrom} — {data.range.dateTo} ·{" "}
        {fill(tt("traffic_freshness"), {
          at: new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "sq-AL", {
            timeZone: data.timezone,
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(data.fetchedAt)),
        })}
      </p>
      {data.warnings.map((key) => (
        <p key={key} className="an-note is-warning">
          {tt(`traffic_warning_${key}`)}
        </p>
      ))}
      {data.status === "empty" ? <Notice title={tt("traffic_noData")}>{tt("traffic_noDataBody")}</Notice> : null}
      <TrafficKpis data={data} tt={tt} lang={lang} />
      <Panel
        title={tt("traffic_trend")}
        hint={tt("traffic_dailyNote")}
        actions={
          <Segmented
            ariaLabel={tt("traffic_trend")}
            value={metric}
            onChange={setMetric}
            options={["activeUsers", "sessions", "view_item", "purchase"].map((value) => ({
              value,
              label: tt(`traffic_${value}`),
            }))}
          />
        }
      >
        <SectionStatus
          data={data}
          section={["activeUsers", "sessions"].includes(metric) ? "trend" : "events"}
          tt={tt}
        />
        <TrendChart
          points={data.trend}
          field={metric}
          granularity="day"
          label={tt(`traffic_${metric}`)}
          money={false}
          showPrevious={false}
          lang={lang}
          tt={tt}
        />
      </Panel>
      <div className="an-grid an-grid--2">
        <Panel title={tt("traffic_acquisition")} hint={tt("traffic_acquisitionNote")}>
          <SectionStatus data={data} section="acquisition" tt={tt} />
          <DataTable
            tt={tt}
            rows={data.acquisition}
            emptyText={tt("traffic_noRows")}
            caption={tt("traffic_acquisition")}
            columns={[
              {
                key: "source",
                label: tt("traffic_source"),
                render: (r) => (
                  <>
                    <strong>{r.source}</strong>
                    <div className="an-muted">
                      {r.channel} · {r.medium}
                    </div>
                  </>
                ),
              },
              numeric("sessions"),
              numeric("engagedSessions"),
              numeric("engagementRate", true),
            ]}
          />
        </Panel>
        <Panel title={tt("traffic_activity")} hint={tt("traffic_activityNote")}>
          <SectionStatus data={data} section="events" tt={tt} />
          <BarList
            rows={activity.filter((e) => e.value !== null)}
            format={(value) => formatNumber(value, lang)}
            emptyText={tt("unavailable")}
          />
          {activity
            .filter((e) => e.value === null)
            .map((e) => (
              <p key={e.key}>
                {e.label}: {tt("unavailable")}
              </p>
            ))}
        </Panel>
      </div>
      <Panel title={tt("traffic_products")} hint={tt("traffic_productsNote")}>
        <SectionStatus data={data} section="products" tt={tt} />
        <DataTable
          rows={data.products}
          caption={tt("traffic_products")}
          emptyText={tt("traffic_noRows")}
          columns={[
            {
              key: "name",
              label: tt("traffic_product"),
              render: (r) => (
                <>
                  <strong>{r.name}</strong>
                  <div className="an-muted">{r.id}</div>
                </>
              ),
            },
            numeric("itemsViewed"),
            numeric("itemsAddedToCart"),
            numeric("itemsPurchased"),
          ]}
        />
      </Panel>
      <div className="an-grid an-grid--2">
        <Panel title={tt("traffic_devices")}>
          <SectionStatus data={data} section="devices" tt={tt} />
          <DataTable
            rows={data.devices}
            emptyText={tt("traffic_noRows")}
            columns={[
              { key: "device", label: tt("traffic_device"), render: (r) => tt(`traffic_device_${r.device}`) },
              numeric("sessions"),
              numeric("engagementRate", true),
            ]}
          />
        </Panel>
        <Panel title={tt("traffic_insights")} hint={tt("traffic_insightsNote")}>
          {data.insights.length ? (
            data.insights.map((insight) => (
              <p key={insight.code}>
                {fill(tt(`traffic_insight_${insight.code}`), {
                  value:
                    insight.code === "sessions_change"
                      ? formatPercent(insight.value, lang)
                      : formatNumber(insight.value, lang),
                })}
              </p>
            ))
          ) : (
            <p className="an-muted">{tt("traffic_noInsights")}</p>
          )}
          <dl>
            <dt>{tt("traffic_averageEngagement")}</dt>
            <dd>
              {formatNumber(data.kpis.averageEngagementSecondsPerSession?.value, lang)} {tt("traffic_seconds")}
            </dd>
            <dt>{tt("traffic_pageViews")}</dt>
            <dd>{formatNumber(data.kpis.screenPageViews?.value, lang)}</dd>
          </dl>
        </Panel>
      </div>
      <SalesComparison data={data} tt={tt} lang={lang} />
    </>
  );
}

export default function TrafficSection({ tt, lang }) {
  const [params, setParams] = useSearchParams();
  const filters = trafficFilters(params);
  const result = useFetchTrafficDashboardQuery(filters, { refetchOnMountOrArgChange: true });
  const query = { ...result, data: result.currentData };
  const data = query.data;
  const available = data && ["live", "partial", "empty"].includes(data.status);
  return (
    <>
      <TrafficControls
        filters={filters}
        range={data?.range}
        tt={tt}
        onChange={(next) => {
          const updated = new URLSearchParams(params);
          updated.set("trafficPreset", next.preset);
          ["dateFrom", "dateTo"].forEach((key) => {
            const param = key === "dateFrom" ? "trafficFrom" : "trafficTo";
            if (next[key]) updated.set(param, next[key]);
            else updated.delete(param);
          });
          setParams(updated);
        }}
      />
      <div className="an-panel__actions">
        <Btn size="sm" icon="refresh" disabled={query.isFetching} onClick={() => query.refetch()}>
          {tt("refreshData")}
        </Btn>
      </div>
      <DataState query={query} tt={tt}>
        {available ? (
          <TrafficDashboard data={data} tt={tt} lang={lang} />
        ) : data ? (
          <Notice tone="warning" title={tt(`traffic_state_${data.status}`)}>
            {tt("traffic_connectionHelp")}
          </Notice>
        ) : null}
      </DataState>
    </>
  );
}
